import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const SkillsTreeSelector = ({
    topicsCategories = [],
    selectedTopicIds = [],
    onToggleTopic
}) => {
    const { t } = useTranslation('common');
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCategories, setExpandedCategories] = useState([]);
    const dropdownRef = useRef(null);

    // Normalize categories and skills safely
    const safeCategories = useMemo(() => {
        let raw = [];
        if (Array.isArray(topicsCategories)) {
            raw = topicsCategories;
        } else if (Array.isArray(topicsCategories?.data)) {
            raw = topicsCategories.data;
        } else if (Array.isArray(topicsCategories?.data?.data)) {
            raw = topicsCategories.data.data;
        }

        return raw.map((cat) => {
            const rawItems = cat.Skills || cat.Topics || [];
            const normalizedSkills = (Array.isArray(rawItems) ? rawItems : [])
                .map((item) => ({
                    SkillId: item.SkillId || item.TopicId || item.Id,
                    SkillName: item.SkillName || item.TopicName || item.Name || '',
                    QuestionCount: item.QuestionCount || 0,
                    CategoryId: cat.CategoryId || item.CategoryId || cat.Id
                }))
                .filter((item) => item.SkillId && item.SkillName);

            return {
                CategoryId: cat.CategoryId || cat.Id,
                CategoryName: cat.CategoryName || cat.Name || '',
                Skills: normalizedSkills
            };
        }).filter((cat) => cat.Skills.length > 0);
    }, [topicsCategories]);

    // Filter categories & skills by search
    const filteredCategories = useMemo(() => {
        if (!searchTerm.trim()) return safeCategories;
        const term = searchTerm.trim().toLowerCase();
        return safeCategories
            .map((cat) => {
                const matchedSkills = (cat.Skills || []).filter((skill) =>
                    (skill.SkillName || '').toLowerCase().includes(term)
                );
                return { ...cat, Skills: matchedSkills };
            })
            .filter((cat) => (cat.Skills || []).length > 0);
    }, [safeCategories, searchTerm]);

    // Group selected skills by category
    const { selectedCategoriesList, totalSelectedCount } = useMemo(() => {
        const categoriesList = [];
        let count = 0;
        const selectedSet = new Set((selectedTopicIds || []).map(String));

        safeCategories.forEach((cat) => {
            if (cat && Array.isArray(cat.Skills)) {
                const matched = cat.Skills.filter((skill) =>
                    selectedSet.has(String(skill.SkillId))
                );
                if (matched.length > 0) {
                    count += matched.length;
                    categoriesList.push({
                        CategoryId: cat.CategoryId,
                        CategoryName: cat.CategoryName,
                        Skills: matched
                    });
                }
            }
        });

        return {
            selectedCategoriesList: categoriesList,
            totalSelectedCount: count
        };
    }, [safeCategories, selectedTopicIds]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleCategoryExpand = (catId) => {
        setExpandedCategories((prev) =>
            prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId]
        );
    };

    return (
        <div className="modern-form-section w-100" style={{ width: '100%' }}>
            <div className="modern-section-title text-primary font-weight-bold d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <i className="icon fal fa-sitemap ml-2 text-primary"></i>
                    <span>{t('QuestionView.skillsTreeSection') || 'المهارات التعليمية المرتبطة'}</span>
                </div>
                <span className="badge badge-pill badge-light text-muted small font-weight-normal border px-2 py-1">
                    {t('QuestionView.skillsTreeNote') || '(شجرة مهارات مع إمكانية البحث)'}
                </span>
            </div>

            <div className="form-group w-100 mb-0">
                <label className="modern-label">
                    {t('QuestionView.skills') || 'مهارات السؤال:'}
                </label>
                <div className="modern-tree-select-wrapper" ref={dropdownRef}>
                    <div
                        className="modern-tree-select-trigger"
                        onClick={() => setShowDropdown((prev) => !prev)}
                    >
                        <div className="modern-tree-select-values">
                            {totalSelectedCount > 0
                                ? `${t('QuestionView.selectedSkillsList') || 'المهارات المختارة'} (${totalSelectedCount})`
                                : (t('QuestionView.skillsTreePlaceholder') || 'اختر المهارات من الشجرة...')}
                        </div>
                        <i className={`fad fa-chevron-down arrow-icon ${showDropdown ? 'open' : ''}`}></i>
                    </div>

                    {showDropdown && (
                        <div className="modern-tree-select-dropdown">
                            <div className="modern-tree-search-wrapper">
                                <input
                                    type="text"
                                    className="form-control modern-search-input"
                                    placeholder={t('QuestionView.searchSkillsPlaceholder') || 'بحث في المهارات...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <i className="fal fa-search search-icon"></i>
                            </div>

                            <div className="modern-tree-nodes">
                                {filteredCategories.map((cat) => {
                                    const isExpanded = searchTerm.trim() ? true : expandedCategories.includes(cat.CategoryId);

                                    return (
                                        <div key={cat.CategoryId} className="modern-tree-category">
                                            <div
                                                className="modern-category-header"
                                                onClick={() => toggleCategoryExpand(cat.CategoryId)}
                                            >
                                                <i
                                                    className={`far ${
                                                        isExpanded ? 'fa-folder-open' : 'fa-folder'
                                                    } category-folder-icon`}
                                                ></i>
                                                <span className="category-name">{cat.CategoryName}</span>
                                                <span className="category-badge">{(cat.Skills || []).length}</span>
                                                <i
                                                    className={`fal fa-chevron-down category-chevron ${
                                                        isExpanded ? 'expanded' : ''
                                                    }`}
                                                ></i>
                                            </div>

                                            {isExpanded && (
                                                <div className="modern-category-children">
                                                    {(cat.Skills || []).map((skill) => {
                                                        const isChecked = (selectedTopicIds || []).map(String).includes(String(skill.SkillId));

                                                        return (
                                                            <label
                                                                key={skill.SkillId}
                                                                className="modern-tree-item"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => onToggleTopic(skill.SkillId)}
                                                                />
                                                                <span className="checkbox-custom"></span>
                                                                <span className="topic-name">{skill.SkillName}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {filteredCategories.length === 0 && (
                                    <div className="text-center text-muted p-2 small">
                                        {t('QuestionView.noSearchResults') || 'لا توجد نتائج مطابقة'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {selectedCategoriesList.length > 0 && (
                    <div className="selected-topics-tags-list mt-3">
                        <div className="selected-topics-label">
                            <i className="far fa-tags ml-1"></i>
                            {t('QuestionView.selectedSkillsList') || 'المهارات المختارة:'}
                        </div>
                        <div className="selected-categories-container">
                            {selectedCategoriesList.map((cat) => (
                                <div key={cat.CategoryId} className="selected-category-group mb-2">
                                    <div className="selected-category-header-title">
                                        <i className="far fa-folder-open ml-1 text-primary"></i>
                                        <span className="category-group-name">{cat.CategoryName}</span>
                                        <span className="selected-category-badge">({cat.Skills.length})</span>
                                    </div>
                                    <div className="selected-tags-container">
                                        {cat.Skills.map((skill) => (
                                            <div key={skill.SkillId} className="selected-topic-chip">
                                                <i className="far fa-bookmark topic-chip-icon"></i>
                                                <span className="topic-chip-name">{skill.SkillName}</span>
                                                <button
                                                    type="button"
                                                    className="topic-chip-remove"
                                                    title={t('Cancel') || 'حذف'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onToggleTopic(skill.SkillId);
                                                    }}
                                                >
                                                    <i className="far fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(SkillsTreeSelector);
